import { createApp } from 'vue'
import { createPinia } from 'pinia'
import ElementPlus from 'element-plus'
import * as ElementPlusIconsVue from '@element-plus/icons-vue'
import 'element-plus/dist/index.css'
import zhCn from 'element-plus/es/locale/lang/zh-cn'
import App from './App.vue'
import router from './router'
import './styles/index.scss'

const app = createApp(App)

// 注册Element Plus图标
for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
  app.component(key, component)
}

app.use(createPinia())
app.use(router)
app.use(ElementPlus, {
  locale: zhCn,
})

// 设置 Vue 全局错误处理器
app.config.errorHandler = (err, instance, info) => {
  const errorMessage = err?.message || ''
  if (errorMessage.includes('getComputedStyle') || 
      errorMessage.includes('parameter 1 is not of type') ||
      errorMessage.includes('ResizeObserver')) {
    // 静默忽略 Element Plus 的 ResizeObserver 错误
    return
  }
  console.error('Vue Error:', err, info)
}

// 设置 Vue 全局警告处理器
app.config.warnHandler = (msg, instance, trace) => {
  if (msg.includes('getComputedStyle') || 
      msg.includes('parameter 1 is not of type') ||
      msg.includes('ResizeObserver')) {
    return
  }
  console.warn('Vue Warning:', msg, trace)
}

// 抑制 ResizeObserver 错误（Element Plus 已知问题）
const debounce = (fn, delay) => {
  let timer = null
  return function () {
    clearTimeout(timer)
    timer = setTimeout(() => fn.apply(this, arguments), delay)
  }
}

// 更彻底的 ResizeObserver 错误处理
if (window.ResizeObserver) {
  const _ResizeObserver = window.ResizeObserver
  
  window.ResizeObserver = class SafeResizeObserver extends _ResizeObserver {
    constructor(callback) {
      // 防抖处理
      const debouncedCallback = debounce(callback, 16)
      
      // 包装回调函数，添加错误处理
      const safeCallback = (entries, observer) => {
        try {
          debouncedCallback(entries, observer)
        } catch (error) {
          // 静默处理 ResizeObserver 错误
          if (!error.message.includes('getComputedStyle') && !error.message.includes('parameter 1 is not of type')) {
            console.warn('ResizeObserver error:', error)
          }
        }
      }
      
      super(safeCallback)
    }

    // 重写 disconnect 方法，确保完全清理
    disconnect() {
      try {
        super.disconnect()
      } catch (error) {
        // 静默处理 disconnect 错误
      }
    }

    // 重写 unobserve 方法，确保安全移除观察
    unobserve(target) {
      try {
        if (target && target.nodeType === Node.ELEMENT_NODE) {
          super.unobserve(target)
        }
      } catch (error) {
        // 静默处理 unobserve 错误
      }
    }
  }
}

// 全局错误处理 - 在 mount 之前就设置
window.addEventListener('error', (event) => {
  // 过滤掉 ResizeObserver 和 Element Plus 相关的错误
  if (event.error && 
      event.error.message && 
      (event.error.message.includes('getComputedStyle') || 
       event.error.message.includes('parameter 1 is not of type') ||
       event.error.message.includes('ResizeObserver'))) {
    event.preventDefault()
    event.stopPropagation()
    return false
  }
}, true) // 使用捕获阶段

window.addEventListener('unhandledrejection', (event) => {
  // 过滤掉 Promise 中的 ResizeObserver 错误
  if (event.reason && 
      event.reason.message && 
      (event.reason.message.includes('getComputedStyle') || 
       event.reason.message.includes('parameter 1 is not of type') ||
       event.reason.message.includes('ResizeObserver'))) {
    event.preventDefault()
    event.stopPropagation()
    return false
  }
}, true)

// 重写 console.error 来过滤特定错误
const originalConsoleError = console.error
console.error = function(...args) {
  const errorMessage = args[0]?.toString() || ''
  if (errorMessage.includes('getComputedStyle') || 
      errorMessage.includes('parameter 1 is not of type') ||
      errorMessage.includes('ResizeObserver')) {
    return // 静默忽略
  }
  originalConsoleError.apply(console, args)
}

// 使用 nextTick 确保 DOM完全准备好
app.mount('#app')